import type RootStore from "../../../stores/RootStore";
import { utils } from "shared/intrasyncUtilsShim";
import { makeAutoObservable, reaction, runInAction } from "mobx";
import { sessionMark } from "stores/sessionGuard";

interface Service {
  path: string;
}


/**
 * Width for a derived DataGrid column.
 *
 * The Entity Viewer builds its columns from whatever keys the service returns,
 * so there is no hand-tuned column list to fall back on — but the KIND of value
 * is known, and that is enough to stop every column claiming the same 200px.
 */
function columnWidthFor(key: string, dataType: string): number {
  const k = key.toLowerCase();
  if (k === "id" || k.endsWith("id")) return 110;
  if (dataType === "boolean") return 90;
  if (dataType === "number") return 110;
  if (dataType === "date" || k.endsWith("at")) return 150;
  // Free text: name-ish columns are what a reader identifies a row by, so
  // they keep room; everything else gets a middling default.
  if (k.includes("name") || k.includes("title") || k.includes("description"))
    return 200;
  return 140;
}

class DatabaseManagerStore {
  rootStore: RootStore;

  loading = false;
  isReady = false;
  availableServices: Service[] = [];

  currentServiceItems: { [key: string]: { total: number; data: unknown[] } } =
    {};

  currentServer = "production";
  currentApp:
    | (RootStore & {
        apiServicesStore?: { find: () => Promise<Service[]> };
      })
    | undefined = undefined;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.currentApp = rootStore;

    makeAutoObservable(this, { rootStore: false, currentApp: false });

    reaction(
      () => this.currentHeaders,
      (currentHeaders) => {
        if (currentHeaders) {
          // console.log('reaction to params', readableJS(currentHeaders));
          if (!this.rootStore.dataGridStore?.namespaces?.entityViewerBody)
            return;

          this.rootStore.dataGridStore.namespaces.entityViewerBody.setColumnHeaders(
            currentHeaders.map((headerKey: string) => {
              let type = "string";
              if (
                headerKey.includes("Date") ||
                headerKey === "removed" ||
                headerKey === "rearViewIFC"
              ) {
                if (!headerKey.includes("EditedBy")) {
                  type = "date";
                }
              }
              if (headerKey.includes("Time")) {
                type = "time";
              }
              if (headerKey === "id" || headerKey === "jobNumber") {
                type = "number";
              }
              if (
                headerKey.includes("Allowed") ||
                headerKey.includes("Archived") ||
                headerKey.includes("archived") ||
                headerKey.includes("Modeled") ||
                headerKey.includes("Drawn") ||
                headerKey.includes("Checked") ||
                headerKey === "required" ||
                headerKey === "adminUser" ||
                headerKey === "isValidated"
              ) {
                type = "boolean";
              }
              return {
                disableEdit:
                  headerKey === "id" || this.selectedService === "channel",
                key: headerKey,
                title: utils.camelToTitle(headerKey),
                dataType: type,
                // A flat 200px per derived column is what makes this view
                // unusable on a phone: across the API's schemas that is a
                // median of 17 columns (3,400px) and up to 47 for `clients`
                // (9,400px) against ~419px of screen — 8x to 22x horizontal
                // scroll. Width the column to what it actually holds: a
                // boolean or a date needs nowhere near a free-text field, and
                // the identifier columns a reader scans by are the ones worth
                // the space.
                width: columnWidthFor(headerKey, type),
              };
            }),
          );
        }
      },
      { delay: 200 },
    );
  }

  /**
   * The Entity Viewer always reads through the app's own RootStore.
   *
   * There used to be a "cloud-dev"/"local-dev" branch here that spun up a
   * SECOND RootStore pointed at another API. It could never have worked: it
   * passed `serviceStores: this.rootStore.serviceStores`, which is the array of
   * derived store NAMES (`"clientsStore"`), not the service CONFIGS, and it
   * omitted `routerStoreProps` — and RootStore throws "Routes List Required"
   * unconditionally when that is missing, so every attempt threw before it
   * could return. Adopting the library composition root additionally requires a
   * constructed Feathers client (`props.app`), which that config also had no
   * way to supply. Removed rather than half-fixed; the matching options are
   * gone from the header's Server select.
   */
  setServer = (server: string) => {
    if (server === this.currentServer) return;
    this.currentServer = "production";
    this.currentServiceItems = {};
    this.availableServices = [];
    this.currentApp = undefined;
    this.currentApp = this.rootStore;
    return this.findServiceStores();
  };

  setupChannelListeners = (servicePath: string) => {
    if (!this.currentApp?.app.service(servicePath)) {
      throw new Error(`That service is not available: ${servicePath}`);
    }
    if (!this.selectedService) {
      throw new Error("No service selected");
    }
    this.currentApp.app.service(servicePath).on("created", (created) => {
      if (this.currentServiceItems[this.selectedService]?.data) {
        this.currentServiceItems[this.selectedService].data.push(created);
      }
    });
    this.currentApp.app.service(servicePath).on("patched", (patched) => {
      if (this.currentServiceItems[this.selectedService]?.data) {
        const foundIndex = this.currentServiceItems[
          this.selectedService
        ].data.findIndex((item) =>
          utils.isSameId((item as { id: string | number }).id, patched.id),
        );
        if (foundIndex > -1) {
          this.currentServiceItems[this.selectedService].data[foundIndex] =
            patched;
        }
      }
    });
    this.currentApp.app.service(servicePath).on("removed", (removed) => {
      if (this.currentServiceItems[this.selectedService]?.data) {
        const newArray = this.currentServiceItems[
          this.selectedService
        ].data.filter(
          (item) =>
            !utils.isSameId((item as { id: string | number }).id, removed.id),
        );
        this.currentServiceItems[this.selectedService].data = newArray;
      }
    });
  };

  setSelectedService = (servicePath: string, needsRouting = true) => {
    if (!this.currentApp?.app.service(servicePath)) {
      throw new Error(`That service is not available: ${servicePath}`);
    }
    if (needsRouting && this.rootStore.routerStore) {
      this.rootStore.routerStore.goTo(null, { servicePath: servicePath });
    }

    // this.selectedService = servicePath;
    this.findServiceItems().then(() => {
      this.setupChannelListeners(servicePath);
      runInAction(() => {
        this.loading = false;
      });
    });
    // Need to add event handlers for created/patched/removed
    return servicePath;
  };

  findServiceStores = async () => {
    if (!this.currentApp?.apiServicesStore) return;
    const foundServices: Service[] =
      (await this.currentApp.apiServicesStore.find().catch((err) => {
        console.log("something went wrong finding services", err);
        return [];
      })) || [];
    if (foundServices?.length > 0) {
      runInAction(() => {
        this.availableServices = foundServices
          ?.slice()
          .filter((i: Service) => {
            if (i.path.includes("autodesk")) return false;
            if (i.path.includes("azure")) return false;
            if (i.path.includes("sage")) return false;
            if (i.path.includes("auth")) return false;
            if (i.path.includes("api-services")) return false;
            return true;
          })
          .sort((a: Service, b: Service) => (a.path < b.path ? -1 : 1));
        if (!this.selectedService) {
          this.setSelectedService(this.availableServices[0].path);
        }
        if (
          this.selectedService &&
          !this.currentServiceItems[this.selectedService]
        ) {
          this.setSelectedService(this.selectedService, false);
        }
      });
    }
  };

  findServiceItems = async () => {
    if (!this.currentApp?.app) return;
    // Every find below swallows its rejection (the session guard's included):
    // a sign-out meanwhile would read as "Found undefined items" for the next
    // person, and the page fill would write into a cache reset() replaced.
    const ended = sessionMark(this.rootStore);
    const foundTotals = await this.currentApp.app
      .service(this.selectedService)
      .find({ query: { $limit: 0 } })
      .catch((err) => {
        console.log("something went wrong finding services", err);
      });
    if (ended()) return;
    if (!foundTotals || foundTotals?.total === 0) {
      runInAction(() => {
        this.currentServiceItems[this.selectedService] = {
          total: 0,
          data: [],
        };
      });
      const emptyReturnNotificationMessage = `Found ${foundTotals.total} items for ${this.selectedService}`;
      return this.rootStore.notificationStore
        ? this.rootStore.notificationStore.addNotification({
            message: emptyReturnNotificationMessage,
            options: { variant: "info" },
          })
        : console.log(emptyReturnNotificationMessage);
    }
    if (foundTotals?.total > 20000) {
      runInAction(() => {
        this.currentServiceItems[this.selectedService] = {
          total: foundTotals?.total,
          data: [],
        };
      });
      return this.rootStore.notificationStore
        ? this.rootStore.notificationStore.addNotification({
            message: `Found ${foundTotals.total} items for ${this.selectedService}, too many to try to fetch, 20k is current max`,
            options: { variant: "warning" },
          })
        : console.log(
            `Found ${foundTotals.total} items for ${this.selectedService}, too many to try to fetch, 20k is current max`,
          );
    }
    this.rootStore.notificationStore
      ? this.rootStore.notificationStore.addNotification({
          message: `Found ${foundTotals.total} items for ${this.selectedService}, fetching all`,
          options: { variant: "info" },
        })
      : console.log(
          `Found ${foundTotals.total} items for ${this.selectedService}, fetching all`,
        );
    const loopCount = Math.ceil(foundTotals?.total / 1000);
    const looper = Array.from(Array(loopCount).keys());

    runInAction(() => {
      this.currentServiceItems[this.selectedService] = {
        total: foundTotals?.total,
        data: [],
      };
    });
    Promise.all(
      looper.map(async (runNumber) => {
        const foundItems = this.currentApp?.app
          ? await this.currentApp.app
              .service(this.selectedService)
              .find({
                query: {
                  $limit: 1000,
                  $skip: 1000 * runNumber,
                },
              })
              .catch((err) => {
                console.log("something went wrong finding services", err);
              })
          : undefined;
        if (foundItems?.data?.length > 0) {
          return foundItems.data;
        }
      }),
    ).then((returnedFromPromises) => {
      if (ended()) return;
      const flattened = returnedFromPromises.flat();
      runInAction(() => {
        this.currentServiceItems[this.selectedService].data = flattened;
      });
    });
  };

  // NOTE: sorting/filtering for the grid lives entirely in the library
  // DataGridStore namespace (`dataGridStore.namespaces.entityViewerBody`) —
  // this store deliberately keeps no parallel sort/filter state. A local
  // sortState/setFilter/handleMultiSort trio existed here until 2026-08-15
  // with zero readers (2026-08-14 audit, F4).

  get selectedService() {
    const { routerStore } = this.rootStore;
    const servicePath = routerStore?.params?.servicePath as string;
    return servicePath || "";
  }

  get availableServiceNames() {
    return this.availableServices?.slice().map((i) => i.path);
  }

  get entities() {

    return this.currentServiceItems[this.selectedService]?.data?.slice() || [];
  }

  get currentHeaders() {
    if (
      !this.currentServiceItems[this.selectedService]?.data ||
      !this.currentServiceItems[this.selectedService]?.data[0]
    )
      return ["id"];
    const firstEntityKeys = Object.keys(
      this.currentServiceItems[this.selectedService]?.data[0] as object,
    );
    if (firstEntityKeys.includes("id")) {
      const currentKeys = firstEntityKeys
        .slice()
        .filter((i) => i !== "id")
        .sort((a, b) => (a < b ? -1 : 1));
      return ["id", ...currentKeys];
    }
    if (firstEntityKeys.includes("connectionId")) {
      const currentKeys = firstEntityKeys
        .slice()
        .filter((i) => i !== "connectionId")
        .sort((a, b) => (a < b ? -1 : 1));
      return ["connectionId", ...currentKeys];
    }
    return [...firstEntityKeys];
  }

  get dataGridStoreCurrentData() {
    if (
      !this.rootStore?.dataGridStore?.namespaces?.entityViewerBody?.currentData
    ) {
      return [];
    }
    const currentData =
      this.rootStore?.dataGridStore?.namespaces?.entityViewerBody?.currentData;
    return currentData;
  }

  get dataGridStoreCurrentDataLength() {
    // return this.entities.length
    return this.dataGridStoreCurrentData.length;
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.loading = false;
    this.isReady = false;
    this.availableServices = [];
    this.currentServiceItems = {};
    this.currentServer = "production";
  }
}

export default DatabaseManagerStore;
